import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import { DiningArea, DiningAreaFormData } from '../types/admin';

export function useDiningAreas() {
  const { company } = useAuthStore();

  return useQuery({
    queryKey: ['pos-dining-areas', company?.id],
    queryFn: async (): Promise<DiningArea[]> => {
      if (!company?.id) return [];

      const { data, error } = await supabase
        .from('pos_dining_areas')
        .select('*')
        .eq('company_id', company.id)
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!company?.id,
  });
}

export function useCreateDiningArea() {
  const queryClient = useQueryClient();
  const { company } = useAuthStore();

  return useMutation({
    mutationFn: async (data: DiningAreaFormData) => {
      if (!company?.id) throw new Error('No company selected');

      // Get max display_order - handle empty result
      const { data: existing, error: orderError } = await supabase
        .from('pos_dining_areas')
        .select('display_order')
        .eq('company_id', company.id)
        .order('display_order', { ascending: false })
        .limit(1);

      // If error is "no rows", that's fine - it means this is the first area
      let nextOrder = 1;
      if (!orderError && existing && existing.length > 0) {
        nextOrder = (existing[0].display_order || 0) + 1;
      }

      const { data: area, error } = await supabase
        .from('pos_dining_areas')
        .insert({
          company_id: company.id,
          ...data,
          display_order: nextOrder,
        })
        .select()
        .single();

      if (error) throw error;
      return area;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pos-dining-areas'] });
    },
  });
}

export function useUpdateDiningArea() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<DiningAreaFormData> }) => {
      const { data: area, error } = await supabase
        .from('pos_dining_areas')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return area;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pos-dining-areas'] });
    },
  });
}

export function useDeleteDiningArea() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('pos_dining_areas')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pos-dining-areas'] });
      queryClient.invalidateQueries({ queryKey: ['pos-tables'] });
    },
  });
}
